import { Button } from '@shared/components/ui/button';
import { DatePicker, ExpiryDatePicker } from '@shared/components/ui/date-picker';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { useToast } from '@shared/hooks/use-toast';
import { format, isValid } from 'date-fns';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateBatch } from '../../../hooks/useBatchMutations';
import type { Batch } from '../../../models/Batch';

interface ExtendedModalCenterRef extends ModalCenterRef {
  openWithBatch: (batch: Batch) => void;
}

const EditBatchModal = forwardRef<ExtendedModalCenterRef, object>((_, ref) => {
  const { t } = useTranslation('batchesModule');
  const { toast } = useToast();
  const updateBatch = useUpdateBatch();
  const internalModalRef = useRef<ModalCenterRef>(null);

  const [batch, setBatch] = useState<Batch | null>(null);
  const [batchNumber, setBatchNumber] = useState('');
  const [receivedAtDate, setReceivedAtDate] = useState<Date | undefined>(undefined);
  const [expiryDate, setExpiryDate] = useState<Date | undefined>(undefined);
  const [supplierReference, setSupplierReference] = useState('');

  const hasActiveStock = (batch?.total_stock ?? 0) > 0;

  useEffect(() => {
    if (batch) {
      setBatchNumber(batch.batch_number);
      if (batch.received_at) {
        const parsed = new Date(batch.received_at.replace(' ', 'T'));
        setReceivedAtDate(isValid(parsed) ? parsed : undefined);
      } else {
        setReceivedAtDate(undefined);
      }
      if (batch.expiry_date) {
        const parsed = new Date(batch.expiry_date + 'T00:00:00');
        setExpiryDate(isValid(parsed) ? parsed : undefined);
      } else {
        setExpiryDate(undefined);
      }
      setSupplierReference(batch.supplier_reference || '');
    }
  }, [batch]);

  const resetForm = () => {
    setBatch(null);
    setBatchNumber('');
    setReceivedAtDate(undefined);
    setExpiryDate(undefined);
    setSupplierReference('');
  };

  const handleCancel = () => {
    internalModalRef.current?.closeModal();
    resetForm();
  };

  const handleSubmit = async () => {
    if (!batch) return;

    try {
      await updateBatch.mutateAsync({
        batchId: batch.id,
        data: {
          ...(!hasActiveStock && {
            batch_number: batchNumber.trim(),
            received_at: receivedAtDate ? format(receivedAtDate, 'yyyy-MM-dd') : '',
          }),
          expiry_date: expiryDate ? format(expiryDate, 'yyyy-MM-dd') : null,
          supplier_reference: supplierReference.trim() || null,
        },
      });

      toast({ title: t('toast.updateSuccess'), variant: 'default' });
      handleCancel();
    } catch (error: any) {
      toast({
        title: t('toast.updateError'),
        description: error?.response?.data?.message || error.message,
        variant: 'destructive',
      });
    }
  };

  const openWithBatch = useCallback((batchData: Batch) => {
    setBatch(batchData);
    internalModalRef.current?.openModal();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      openModal: () => internalModalRef.current?.openModal(),
      closeModal: () => internalModalRef.current?.closeModal(),
      openWithBatch,
    }),
    [openWithBatch],
  );

  return (
    <ModalCenter ref={internalModalRef} title={t('modals.edit.title')} type="info">
      <div className="space-y-4">
        {hasActiveStock && (
          <p className="text-sm text-warning bg-bg-warning rounded-md px-3 py-2">{t('modals.edit.restricted')}</p>
        )}

        <div className="space-y-2">
          <label className="text-sm font-medium text-text">{t('modals.create.batch_number')} *</label>
          <Input value={batchNumber} onChange={(e) => setBatchNumber(e.target.value)} disabled={hasActiveStock} />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-text">{t('modals.create.received_at')} *</label>
          <DatePicker
            date={receivedAtDate}
            onDateChange={setReceivedAtDate}
            placeholder={t('modals.create.date_placeholder')}
            disabled={hasActiveStock}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-text">{t('modals.create.expiry_date')}</label>
          <ExpiryDatePicker
            date={expiryDate}
            onDateChange={setExpiryDate}
            fromYear={new Date().getFullYear()}
            toYear={new Date().getFullYear() + 20}
            labels={{
              day: t('modals.create.expiry_day'),
              month: t('modals.create.expiry_month'),
              year: t('modals.create.expiry_year'),
              preview: t('modals.create.expiry_preview'),
              search: t('modals.create.expiry_search'),
              notFound: t('modals.create.expiry_not_found'),
            }}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-text">{t('modals.create.supplier_reference')}</label>
          <Input
            value={supplierReference}
            onChange={(e) => setSupplierReference(e.target.value)}
            placeholder={t('modals.create.supplier_reference_placeholder')}
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="lg" onClick={handleCancel} disabled={updateBatch.isPending}>
            {t('modals.edit.cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            className="text-white"
            onClick={handleSubmit}
            disabled={updateBatch.isPending}
          >
            {t('modals.edit.submit')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

EditBatchModal.displayName = 'EditBatchModal';

export default EditBatchModal;
